import { IQuery, IQueryMediator } from 'core-ts-types';
import * as _4 from 'lodash4';
import { Strategy } from 'utility-ts-strategy';
import { IPlanFeebackCourseGroupsDatasource } from '../datasources/plan_feedback_course_groups_datasource';
import { IPlanFeebackCoursesDatasource } from '../datasources/plan_feedback_courses_datasource';
import { IPlanFeedbackProgramSlotsDatasource } from '../datasources/plan_feedback_program_slots_datasource';
import { IPlanFeebackTermsDatasource } from '../datasources/plan_feedback_terms_datasource';
import { IFeedbackTransaction } from './../builders/plan_feedback_transaction_builder';
import {
  FeedbackSelectedLinkedSuggestionModel,
  IFeedbackSelectedLinkedSuggestionModel
} from './../models/feedback_selected_linked_suggestion_model';
import { PlanFeedbackServant } from './../servants/plan_feedback_servant';
import { PlanFeedbackTermFieldIdentifierStrategy } from './../strategies/plan_feedback_transaction_types_query_strategy';

export class APFeedbackLinkedSuggestionsQuery implements IQuery {
  private __result: any;

  constructor(
    QueryMediator: IQueryMediator,
    private ngPlanFeebackTermsDatasource: IPlanFeebackTermsDatasource,
    private ngPlanFeebackCoursesDatasource: IPlanFeebackCoursesDatasource,
    private ngFeedbackSelectedLinkedSuggestionModel: FeedbackSelectedLinkedSuggestionModel,
    private ngPlanFeebackCourseGroupsDatasource: IPlanFeebackCourseGroupsDatasource,
    private ngPlanFeedbackProgramSlotsDatasource: IPlanFeedbackProgramSlotsDatasource
  ) {
    QueryMediator.registerQuery(this, ngFeedbackSelectedLinkedSuggestionModel);
  }

  getHeaderTextForCourse(nks: IFeedbackSelectedLinkedSuggestionModel['nks']) {
    if (!nks) {
      return '';
    }
    const correspondingCourse = _4.find(this.ngPlanFeebackCoursesDatasource.metasource().derived(), {
      nk: _4.first(nks)
    });
    const termField = PlanFeedbackTermFieldIdentifierStrategy[correspondingCourse.diff].currentTermField;
    const courseName = _4.get(correspondingCourse, `_${termField}.name`);
    return `${this.getTextAgainstAction()} ${correspondingCourse.course.cd} in ${courseName}`;
  }

  getHeaderTextForCourseGroup(nks: IFeedbackSelectedLinkedSuggestionModel['nks']) {
    if (!nks) {
      return '';
    }
    const correspondingCourseGroup = _4.find(this.ngPlanFeebackCourseGroupsDatasource.metasource().derived(), {
      nk: _4.first(nks)
    });
    const termField = PlanFeedbackTermFieldIdentifierStrategy[correspondingCourseGroup.diff].currentTermField;
    const courseGroupName = _4.get(correspondingCourseGroup, `_${termField}.name`);
    return `${this.getTextAgainstAction()} ${correspondingCourseGroup.program_slot_obj.desc} in ${courseGroupName}`;
  }

  getTextAgainstAction(withSuffix: boolean = false) {
    return new Strategy({
      ACCEPT: `Accept${withSuffix ? 'ing' : ''}`,
      REJECT: `Declin'${withSuffix ? 'ing' : 'e'}`,
      fallback: 'ACCEPT'
    }).get(this.ngFeedbackSelectedLinkedSuggestionModel.read().action as string);
  }

  getHeaderTextForTerm(nks: IFeedbackSelectedLinkedSuggestionModel['nks']) {
    if (!nks) {
      return '';
    }
    const feedbackTerm = _4.find(this.ngPlanFeebackTermsDatasource.read(), { nk: _4.first(nks) });
    return `You've accepted ${feedbackTerm.term.name}`;
  }

  getHeaderText() {
    const linkedSuggestion = this.ngFeedbackSelectedLinkedSuggestionModel.read();
    const nks = linkedSuggestion.nks;
    const type = linkedSuggestion.type as NonNullable<typeof linkedSuggestion.type>;
    if (type === 'COURSE') {
      return this.getHeaderTextForCourse(nks);
    } else if (type === 'COURSE_GROUP') {
      return this.getHeaderTextForCourseGroup(nks);
    } else if (type === 'TERM') {
      // TERM
      return this.getHeaderTextForTerm(nks);
    } else {
      // if(type === 'TEMPLATE')
      return `You've accepted template changes`;
    }
  }

  getSuggestions() {
    const linkedSuggestion = this.ngFeedbackSelectedLinkedSuggestionModel.read();
    const resolutionIds = linkedSuggestion.resolutionIds as number[];
    const nks = linkedSuggestion.nks;
    const isTermType = linkedSuggestion.type === 'TERM';
    const feedbackTerm =
      isTermType && nks && !_4.isEmpty(nks)
        ? _4.find(this.ngPlanFeebackTermsDatasource.read(), { nk: _4.first(nks) })
        : undefined;

    const _feedbackCourses = this.ngPlanFeebackCoursesDatasource.metasource().derived();

    const feedbackCourses = _4.filter(
      _feedbackCourses,
      (feedbackCourse) =>
        PlanFeedbackServant.isValidFeedback(feedbackCourse) && _4.includes(resolutionIds, feedbackCourse.resolution)
    );

    const feedbackLinkedDiffTermCourses = _4.filter(feedbackCourses, (feedbackCourse) => {
      const termField = PlanFeedbackTermFieldIdentifierStrategy[feedbackCourse.diff].currentTermField;
      return isTermType && feedbackTerm && feedbackCourse[termField] !== feedbackTerm.term.id;
    });

    const _feedbackCourseGroups = this.ngPlanFeebackCourseGroupsDatasource.metasource().derived();

    const feedbackCourseGroups = _4.filter(
      _feedbackCourseGroups,
      (feedbackCourseGroup) =>
        PlanFeedbackServant.isValidFeedback(feedbackCourseGroup) &&
        _4.includes(resolutionIds, feedbackCourseGroup.resolution)
    );

    const feedbackLinkedDiffTermCourseGroups = _4.filter(feedbackCourseGroups, (feedbackCourseGroup) => {
      const termField = PlanFeedbackTermFieldIdentifierStrategy[feedbackCourseGroup.diff].currentTermField;
      return isTermType && feedbackTerm && feedbackCourseGroup[termField] !== feedbackTerm.term.id;
    });

    const feedbackProgramSlots = this.ngPlanFeedbackProgramSlotsDatasource
      .values()
      .filter(
        (feedbackProgramSlot) =>
          PlanFeedbackServant.isValidFeedback(feedbackProgramSlot) &&
          _4.includes(resolutionIds, feedbackProgramSlot.resolution)
      );

    return {
      allSuggestions: PlanFeedbackServant.getSuggestionsForCourses(feedbackCourses).concat(
        PlanFeedbackServant.getSuggestionsForCourseGroups(feedbackCourseGroups).concat(
          PlanFeedbackServant.getSuggestionsForProgramSlots(feedbackProgramSlots)
        )
      ),
      suggestionsOutSideOfTerm: PlanFeedbackServant.getSuggestionsForCourses(feedbackLinkedDiffTermCourses).concat(
        PlanFeedbackServant.getSuggestionsForCourseGroups(feedbackLinkedDiffTermCourseGroups)
      )
    };
  }

  getMiddleText() {
    const diffTextsForMiddleText = {
      COURSE_GROUP: 'course group',
      COURSE: 'course',
      TERM: 'term',
      TEMPLATE: 'template changes',
      PROGRAM_SLOT: 'template changes'
    };
    return `By ${_4.toLower(this.getTextAgainstAction(true))} this ${
      diffTextsForMiddleText[this.ngFeedbackSelectedLinkedSuggestionModel.read().type as string]
    } the following courses/course groups/template changes will also be updated: `;
  }

  declineDisabled(suggestions: IFeedbackTransaction[], type: IFeedbackSelectedLinkedSuggestionModel['type']) {
    return type === 'TERM' || _4.find(suggestions, { isLocked: true });
  }

  getLinkedSuggestionsOnly(allSuggestions: IFeedbackTransaction[], suggestionsOutSideOfTerm: IFeedbackTransaction[]) {
    if (this.ngFeedbackSelectedLinkedSuggestionModel.read().type === 'TERM') {
      return suggestionsOutSideOfTerm;
    } else {
      const nks = this.ngFeedbackSelectedLinkedSuggestionModel.read().nks as NonNullable<
        IFeedbackSelectedLinkedSuggestionModel['nks']
      >;
      return _4.reject(allSuggestions, (suggestion) => _4.includes(nks, suggestion.transactionNK));
    }
  }

  run(): this {
    const action = this.ngFeedbackSelectedLinkedSuggestionModel.read().action;
    const suggestions = this.getSuggestions();
    this.__result = {
      linkedSuggestions: this.getLinkedSuggestionsOnly(
        suggestions.allSuggestions,
        suggestions.suggestionsOutSideOfTerm
      ),
      headerText: this.getHeaderText(),
      middleText: this.getMiddleText(),
      declineDisabled:
        this.declineDisabled(suggestions.allSuggestions, this.ngFeedbackSelectedLinkedSuggestionModel.read().type) ||
        action === 'ACCEPT',
      acceptDisabled: action === 'REJECT',
      suggestions: suggestions.allSuggestions
    };
    return this;
  }

  get result() {
    return this.__result;
  }
}

angular
  .module('capability-ng-academic-planner')
  .service('ngAPFeedbackLinkedSuggestionsQuery', APFeedbackLinkedSuggestionsQuery);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/suggestions/components/plan_feedback/queries/feedback_linked_suggestions_query.ts